import type { FunctionComponent } from 'react';
import { lazy, Suspense } from 'react';
import { z } from 'zod';

import type { AgentLibraryScope } from '../fetchers/agents/recommended-agents-fetchers';
import {
    createUrlTileCodec,
    createUrlTileDefinition,
    type UrlTileModule,
    type UrlTileRenderContext,
} from '../url-tiles';

export const AGENT_ATTRIBUTIONS_TILE_TYPE = 'v1.agents.attributions' as const;
export const AGENT_ATTRIBUTIONS_TILE_SLOT_KEY = 'agents.attributions';
const TILE_WIDTH = '480px';

const idSchema = z
    .string()
    .trim()
    .min(1)
    .refine((value) => !value.includes('.'));

export const agentAttributionsTileSchema = z.object({
    agentId: idSchema,
    architectureId: idSchema,
    kind: z.enum(['job', 'position']),
    type: z.literal(AGENT_ATTRIBUTIONS_TILE_TYPE),
});

export type AgentAttributionsTilePayload = z.infer<typeof agentAttributionsTileSchema>;

export const createAgentAttributionsTilePayload = (
    { architectureId, kind }: AgentLibraryScope,
    agentId: string,
): AgentAttributionsTilePayload => ({ agentId, architectureId, kind, type: AGENT_ATTRIBUTIONS_TILE_TYPE });

/** The agent whose panel [payload] opens, when it opens one of the architecture in [scope]. */
export const selectAgentIdFromTile = (payload: unknown, scope: AgentLibraryScope): string | undefined => {
    const parsed = agentAttributionsTileSchema.safeParse(payload);

    return parsed.success && parsed.data.kind === scope.kind && parsed.data.architectureId === scope.architectureId
        ? parsed.data.agentId
        : undefined;
};

type TileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: AgentAttributionsTilePayload;
};

// Loaded on demand: the panel reaches into the job architecture and position features, whose barrels import this one.
const AttributionsUrlTile = lazy(async () => {
    const { AttributionsUrlTile } = await import('./library/AttributionsUrlTile');

    return { default: AttributionsUrlTile };
});

const LazyAttributionsUrlTile: FunctionComponent<TileProps> = (props) => (
    <Suspense fallback={null}>
        <AttributionsUrlTile {...props} />
    </Suspense>
);

export const agentAttributionsUrlTileDefinition = createUrlTileDefinition({
    codec: createUrlTileCodec<AgentAttributionsTilePayload>({
        options: [],
        segments: ['kind', 'architectureId', 'agentId'],
        type: AGENT_ATTRIBUTIONS_TILE_TYPE,
    }),
    maxWidth: TILE_WIDTH,
    minWidth: TILE_WIDTH,
    render: (props: TileProps) => <LazyAttributionsUrlTile {...props} />,
    schema: agentAttributionsTileSchema,
    slotKey: () => AGENT_ATTRIBUTIONS_TILE_SLOT_KEY,
    tileKey: ({ agentId, architectureId, kind }) =>
        `${AGENT_ATTRIBUTIONS_TILE_SLOT_KEY}.${kind}.${architectureId}.${agentId}`,
    toProps: (payload, context) => ({ context, payload }),
    width: TILE_WIDTH,
});

export const agentsUrlTileModule: UrlTileModule = {
    definitions: [agentAttributionsUrlTileDefinition],
};
